/**
 * Vendor payment-plan (maksekokkulepe) schedule math for Progear Finance 2.0.
 *
 * This is the ONE definition of how a supplier payment agreement's schedule is
 * generated, validated and paid down (replit.md master rule: one truth per
 * number; L9: integer cents only). The API materializes these rows into
 * ordinary `cash_flow_entry` records (`pplan:<plan>:<seq>:<part>`); no
 * component ever recomputes an installment.
 *
 * Money discipline (L9):
 *  - All inputs and outputs are integer cents.
 *  - Floating point is used ONLY for the per-row interest product
 *    `balance × i`, rounded to whole cents exactly once.
 *  - The running balance is reduced with integer subtraction and the LAST
 *    installment absorbs the rounding residual, so Σ principal always equals
 *    the plan total exactly. Golden-tested.
 *
 * Stored rows are the truth (like 0046 loan contract imports): the user may
 * hand-edit dates and amounts after generation, so consumers read the STORED
 * rows and this module only validates the chain (`validatePaymentPlanSchedule`).
 * Interest per row is a stored fact of the agreement — rescheduling a row does
 * NOT recompute it.
 *
 * Modes (UI labels Estonian; codes English per replit.md terminology):
 *  - `equal_principal` — võrdne põhiosa: the total split evenly over
 *    `termMonths`, declining interest on the balance.
 *  - `fixed_principal` — fikseeritud põhiosa: a fixed principal amount each
 *    month until the residual (≤ the fixed amount) closes the plan.
 */
import { addMonthsClamped } from './loan';

// ------------------------------------------------------------------- terms

export const PAYMENT_PLAN_MODES = ['equal_principal', 'fixed_principal'] as const;
export type PaymentPlanMode = (typeof PAYMENT_PLAN_MODES)[number];

/** Hard cap on generated schedule length (10 years — far beyond any vendor plan). */
export const MAX_PLAN_MONTHS = 120;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Terms of a payment agreement. Mirrors pgfin.vendor_payment_plan. */
export interface PaymentPlanTerms {
  /** The debt going into the schedule, integer cents > 0. */
  totalCents: number;
  /** Agreed monthly interest on the DECLINING balance, basis points (150 = 1.5%/kuu). */
  monthlyRateBps: number;
  /** First installment due day (ISO, nominal — no working-day shift). */
  firstDueDate: string;
  mode: PaymentPlanMode;
  /** Required for `equal_principal`; forbidden for `fixed_principal`. */
  termMonths?: number;
  /** Required for `fixed_principal`; forbidden for `equal_principal`. */
  fixedPrincipalCents?: number;
}

/** One schedule row. `remainingCents` is the balance AFTER the row's principal. */
export interface PaymentPlanInstallment {
  /** 1-based, contiguous. */
  seq: number;
  dueDate: string;
  principalCents: number;
  interestCents: number;
  /** principal + interest — the row's L9 identity. */
  totalCents: number;
  remainingCents: number;
}

export interface PaymentPlanTotals {
  principalCents: number;
  interestCents: number;
  totalCents: number;
}

// -------------------------------------------------------------- assertions

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function assertIsoCalendarDay(iso: string, label: string): void {
  if (!ISO_DATE.test(iso)) {
    throw new RangeError(`${label}: expected an ISO date (YYYY-MM-DD), received ${iso}`);
  }
  const [y, m, d] = iso.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) {
    throw new RangeError(`${label}: ${iso} is not a real calendar day`);
  }
}

function assertPositiveInt(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${label}: expected a positive integer, received ${value}`);
  }
}

function assertNonNegativeInt(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label}: expected a non-negative integer, received ${value}`);
  }
}

/** Validate a full set of plan terms; throws RangeError on any bad field. */
export function assertPaymentPlanTerms(terms: PaymentPlanTerms): void {
  assertPositiveInt(terms.totalCents, 'totalCents');
  if (
    !Number.isSafeInteger(terms.monthlyRateBps) ||
    terms.monthlyRateBps < 0 ||
    terms.monthlyRateBps > 10000
  ) {
    throw new RangeError(
      `monthlyRateBps: expected an integer between 0 and 10000 (basis points per month), received ${terms.monthlyRateBps}`,
    );
  }
  assertIsoCalendarDay(terms.firstDueDate, 'firstDueDate');
  if (!PAYMENT_PLAN_MODES.includes(terms.mode)) {
    throw new RangeError(`mode: unknown mode ${String(terms.mode)}`);
  }
  if (terms.mode === 'equal_principal') {
    if (terms.termMonths === undefined) {
      throw new RangeError('termMonths: required for an equal_principal plan');
    }
    assertPositiveInt(terms.termMonths, 'termMonths');
    if (terms.termMonths > MAX_PLAN_MONTHS) {
      throw new RangeError(`termMonths: expected at most ${MAX_PLAN_MONTHS}, received ${terms.termMonths}`);
    }
    if (terms.fixedPrincipalCents !== undefined) {
      throw new RangeError('fixedPrincipalCents: only a fixed_principal plan may carry a fixed amount');
    }
  } else {
    if (terms.fixedPrincipalCents === undefined) {
      throw new RangeError('fixedPrincipalCents: required for a fixed_principal plan');
    }
    assertPositiveInt(terms.fixedPrincipalCents, 'fixedPrincipalCents');
    if (terms.termMonths !== undefined) {
      throw new RangeError('termMonths: only an equal_principal plan may carry a term');
    }
    const impliedTerm = Math.ceil(terms.totalCents / terms.fixedPrincipalCents);
    if (impliedTerm > MAX_PLAN_MONTHS) {
      throw new RangeError(
        `fixedPrincipalCents: ${terms.fixedPrincipalCents} implies ${impliedTerm} months — expected at most ${MAX_PLAN_MONTHS}`,
      );
    }
  }
}

// -------------------------------------------------------------- generation

/**
 * Generate the agreement's schedule from its terms.
 *
 * Invariants (golden-tested):
 *  - monthly nominal due dates from `firstDueDate` (month-length clamped);
 *  - `Σ principalCents === terms.totalCents` for both modes;
 *  - `remainingCents` is strictly decreasing and ends at 0;
 *  - interest per row = round(balance-before × monthlyRate), so a 0-rate plan
 *    is a pure principal split;
 *  - the LAST row absorbs the rounding residual (equal) or the sub-fixed
 *    remainder (fixed).
 */
export function paymentPlanSchedule(terms: PaymentPlanTerms): PaymentPlanInstallment[] {
  assertPaymentPlanTerms(terms);
  const monthlyRate = terms.monthlyRateBps / 10000;
  const termMonths =
    terms.mode === 'equal_principal'
      ? (terms.termMonths as number)
      : Math.ceil(terms.totalCents / (terms.fixedPrincipalCents as number));
  const evenPrincipal =
    terms.mode === 'equal_principal' ? Math.floor(terms.totalCents / termMonths) : 0;

  const rows: PaymentPlanInstallment[] = [];
  let balance = terms.totalCents;
  for (let seq = 1; seq <= termMonths; seq += 1) {
    const last = seq === termMonths;
    const interestCents = Math.round(balance * monthlyRate);
    const principalCents = last
      ? balance
      : terms.mode === 'equal_principal'
        ? evenPrincipal
        : (terms.fixedPrincipalCents as number);
    balance -= principalCents;
    rows.push({
      seq,
      dueDate: addMonthsClamped(terms.firstDueDate, seq - 1),
      principalCents,
      interestCents,
      totalCents: principalCents + interestCents,
      remainingCents: balance,
    });
  }
  return rows;
}

/** Sum a schedule's components (one truth for preview/detail footers). */
export function paymentPlanTotals(rows: readonly PaymentPlanInstallment[]): PaymentPlanTotals {
  let principalCents = 0;
  let interestCents = 0;
  for (const row of rows) {
    principalCents += row.principalCents;
    interestCents += row.interestCents;
  }
  return { principalCents, interestCents, totalCents: principalCents + interestCents };
}

// -------------------------------------------------------------- validation

/**
 * Validate STORED (possibly hand-edited) schedule rows against the plan's
 * total. Throws RangeError (with the failing row's seq) on the first
 * violation. Checks, in order:
 *
 *  1. non-empty, at most MAX_PLAN_MONTHS rows;
 *  2. seq contiguity (1…n in order) — duplicates/gaps corrupt external_ids;
 *  3. real calendar due days, strictly increasing;
 *  4. per-row: integer cents, principal ≥ 0, interest ≥ 0, total > 0 and
 *     principal + interest = total (the L9 row identity);
 *  5. Σ principal = the plan's totalCents (the balance walk ends at exactly 0).
 *
 * What it deliberately does NOT check: the interest column against the plan's
 * rate — after a hand edit the stored interest is the agreed fact.
 */
export function validatePaymentPlanSchedule(
  rows: readonly PaymentPlanInstallment[],
  expect: { totalCents: number },
): void {
  assertPositiveInt(expect.totalCents, 'totalCents');
  if (rows.length === 0) {
    throw new RangeError('schedule: expected at least one installment row');
  }
  if (rows.length > MAX_PLAN_MONTHS) {
    throw new RangeError(`schedule: expected at most ${MAX_PLAN_MONTHS} rows, received ${rows.length}`);
  }
  let balance = expect.totalCents;
  let prevDue: string | null = null;
  rows.forEach((row, i) => {
    const expectSeq = i + 1;
    if (row.seq !== expectSeq) {
      throw new RangeError(`row ${expectSeq}: seq must be contiguous — expected ${expectSeq}, received ${row.seq}`);
    }
    assertIsoCalendarDay(row.dueDate, `row ${row.seq}: dueDate`);
    if (prevDue !== null && row.dueDate <= prevDue) {
      throw new RangeError(
        `row ${row.seq}: dueDate ${row.dueDate} must be after the previous row's ${prevDue}`,
      );
    }
    prevDue = row.dueDate;
    assertNonNegativeInt(row.principalCents, `row ${row.seq}: principalCents`);
    assertNonNegativeInt(row.interestCents, `row ${row.seq}: interestCents`);
    assertPositiveInt(row.totalCents, `row ${row.seq}: totalCents`);
    if (row.principalCents + row.interestCents !== row.totalCents) {
      throw new RangeError(
        `row ${row.seq}: principal + interest must equal total (${row.principalCents} + ${row.interestCents} != ${row.totalCents})`,
      );
    }
    if (row.principalCents > balance) {
      throw new RangeError(
        `row ${row.seq}: principal ${row.principalCents} exceeds the remaining balance ${balance}`,
      );
    }
    balance -= row.principalCents;
  });
  if (balance !== 0) {
    throw new RangeError(
      `schedule: principals must sum to the plan total — ${balance} cents left unscheduled`,
    );
  }
}

// -------------------------------------------------------------- allocation

/** One invoice's share of a received payment (mark-paid allocation). */
export interface PaymentAllocation {
  entryId: string;
  amountCents: number;
}

export interface PaymentAllocationResult {
  /** Per-invoice allocations in input order; zero-amount invoices are omitted. */
  allocations: PaymentAllocation[];
  /** The part of the payment no invoice could absorb (0 when fully placed). */
  remainderCents: number;
}

/**
 * Allocate a received installment payment across the plan's covered invoices,
 * OLDEST FIRST — the caller passes the invoices already ordered (oldest due
 * first); this function is pure and deterministic. Each invoice absorbs up to
 * its outstanding amount; whatever cannot be placed is returned as
 * `remainderCents` (the API surfaces it, never silently drops it). The result
 * rows become settlement adjustments (`adjustment_type 'payment_plan'`) on the
 * covered entries — the plan itself never owns a payment-history table.
 */
export function allocatePaymentOldestFirst(
  paymentCents: number,
  invoices: readonly { entryId: string; outstandingCents: number }[],
): PaymentAllocationResult {
  assertPositiveInt(paymentCents, 'paymentCents');
  const allocations: PaymentAllocation[] = [];
  let remaining = paymentCents;
  for (const invoice of invoices) {
    assertNonNegativeInt(invoice.outstandingCents, `outstandingCents (${invoice.entryId})`);
    if (remaining === 0) break;
    if (invoice.outstandingCents === 0) continue;
    const amount = Math.min(remaining, invoice.outstandingCents);
    allocations.push({ entryId: invoice.entryId, amountCents: amount });
    remaining -= amount;
  }
  return { allocations, remainderCents: remaining };
}

// ------------------------------------------------- installment entry identity

/**
 * The component parts an installment materializes into calendar entries as
 * (`pplan:<plan>:<seq>:<part>`). Display order is fixed: principal, interest —
 * the same order the materializer emits them in. Principal lands on the plan's
 * category; interest ALWAYS lands on financial_cost.
 */
export const PAYMENT_PLAN_INSTALLMENT_PARTS = ['principal', 'interest'] as const;
export type PaymentPlanInstallmentPart = (typeof PAYMENT_PLAN_INSTALLMENT_PARTS)[number];

/** Identity parsed from a materialized installment entry's external id. */
export interface PaymentPlanInstallmentEntryRef {
  planId: string;
  /** 1-based installment number (the `<seq>` segment). */
  seq: number;
  part: PaymentPlanInstallmentPart;
}

const PLAN_INSTALLMENT_EXTERNAL_ID = /^pplan:(.+):(\d+):(principal|interest)$/;

/**
 * Parse a materialized installment entry key (`pplan:<plan>:<seq>:<part>`)
 * into its identity, or null for anything else. The ONE definition of the key
 * shape — the materializer builds it, every consumer parses it from here.
 */
export function parsePaymentPlanExternalId(
  externalId: string | null,
): PaymentPlanInstallmentEntryRef | null {
  if (externalId === null) return null;
  const match = PLAN_INSTALLMENT_EXTERNAL_ID.exec(externalId);
  if (match === null) return null;
  const seq = Number(match[2]);
  // The materializer only emits seq >= 1 — a `:0:` key is corrupt, not an installment.
  if (seq < 1) return null;
  return { planId: match[1], seq, part: match[3] as PaymentPlanInstallmentPart };
}

/** Build one component entry's external id (`pplan:<plan>:<seq>:<part>`). */
export function paymentPlanInstallmentExternalId(
  planId: string,
  seq: number,
  part: PaymentPlanInstallmentPart,
): string {
  assertPositiveInt(seq, 'seq');
  return `pplan:${planId}:${seq}:${part}`;
}

/**
 * The shared external-id prefix of ONE plan's materialized entries
 * (`pplan:<plan>:`) — a LIKE on `<prefix>%` finds every entry of the plan and
 * nothing else.
 */
export function paymentPlanExternalIdPrefix(planId: string): string {
  return `pplan:${planId}:`;
}
